/**
 * MyPodOps Studio V2 - Main Application
 *
 * Browser-based remote recording tool competing with Riverside.fm
 * Features: 4K video, 48kHz audio, multi-track recording, AI editing
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { create } from 'zustand';
import { GreenRoom } from './components/GreenRoom';
import { StudioRoom } from './components/StudioRoom';
import { RecordingsPage } from './components/RecordingsPage';
import { GoLive } from './pages/GoLive';
import { SessionConfig, SessionState } from './lib/session';

// Global state store
interface StudioStore {
  // Auth
  token: string | null;
  userId: string | null;
  userName: string | null;

  // Session
  sessionConfig: SessionConfig | null;
  sessionState: SessionState;

  // Actions
  setAuth: (token: string, userId: string, userName: string) => void;
  setSessionConfig: (config: SessionConfig) => void;
  setSessionState: (state: SessionState) => void;
  clearSession: () => void;
}

// Helper to safely parse JSON from sessionStorage
function getStoredSessionConfig(): SessionConfig | null {
  try {
    const stored = sessionStorage.getItem('studioSessionConfig');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

// Helper to get userId from various possible localStorage keys
function getStoredUserId(): string | null {
  // Try direct keys first
  const directKeys = ['userId', 'user_id', 'id', 'uid', 'userid'];
  for (const key of directKeys) {
    const value = localStorage.getItem(key);
    if (value && value !== 'null' && value !== 'undefined') {
      console.log('[App] Found userId in localStorage key:', key, '=', value);
      return value;
    }
  }

  // Try parsing user object from localStorage - include MyPodOps main site keys
  const userObjectKeys = ['podops_user', 'userData', 'user', 'currentUser', 'authUser'];
  for (const key of userObjectKeys) {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        const id = parsed?.id || parsed?.userId || parsed?.user_id;
        if (id) {
          console.log('[App] Found userId in localStorage user object:', key, '=', id);
          return String(id);
        }
      }
    } catch {
      // JSON parse failed, continue
    }
  }

  // Try sessionStorage as well - include user object keys
  const sessionKeys = ['userId', 'user_id', 'id'];
  for (const key of sessionKeys) {
    const value = sessionStorage.getItem(key);
    if (value && value !== 'null' && value !== 'undefined') {
      console.log('[App] Found userId in sessionStorage key:', key, '=', value);
      return value;
    }
  }

  // Try sessionStorage user objects
  const sessionUserKeys = ['podops_user', 'userData', 'user'];
  for (const key of sessionUserKeys) {
    try {
      const stored = sessionStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        const id = parsed?.id || parsed?.userId || parsed?.user_id;
        if (id) {
          console.log('[App] Found userId in sessionStorage user object:', key, '=', id);
          return String(id);
        }
      }
    } catch {
      // JSON parse failed, continue
    }
  }

  // Try to decode userId from JWT token as fallback
  const token = localStorage.getItem('authToken') || localStorage.getItem('token') || sessionStorage.getItem('authToken');
  if (token) {
    try {
      // JWT tokens are base64url encoded, split by dots: header.payload.signature
      const parts = token.split('.');
      if (parts.length === 3) {
        // Decode payload (second part)
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        const id = payload?.id || payload?.userId || payload?.user_id || payload?.sub;
        if (id) {
          console.log('[App] Found userId in JWT token:', id);
          return String(id);
        }
      }
    } catch {
      // JWT decode failed, continue
    }
  }

  console.warn('[App] Could not find userId in localStorage or sessionStorage');
  return null;
}

export const useStudioStore = create<StudioStore>((set) => ({
  // Check both authToken (main site) and token keys, also check sessionStorage
  token: sessionStorage.getItem('authToken') || localStorage.getItem('authToken') || localStorage.getItem('token'),
  userId: getStoredUserId(),
  userName: localStorage.getItem('userName') || localStorage.getItem('user_name'),
  // Hydrate sessionConfig from sessionStorage to survive navigation/refresh
  sessionConfig: getStoredSessionConfig(),
  sessionState: 'idle',

  setAuth: (token, userId, userName) => {
    // Store to both keys for compatibility
    localStorage.setItem('authToken', token);
    localStorage.setItem('token', token);
    localStorage.setItem('userId', userId);
    localStorage.setItem('userName', userName);
    set({ token, userId, userName });
  },

  setSessionConfig: (config) => {
    // Persist to sessionStorage so it survives navigation/refresh
    if (config) {
      sessionStorage.setItem('studioSessionConfig', JSON.stringify(config));
    } else {
      sessionStorage.removeItem('studioSessionConfig');
    }
    set({ sessionConfig: config });
  },
  setSessionState: (state) => set({ sessionState: state }),

  clearSession: () => {
    // Clear from sessionStorage when session ends
    sessionStorage.removeItem('studioSessionConfig');
    set({
      sessionConfig: null,
      sessionState: 'idle'
    });
  },
}));

// Auth check wrapper
function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = useStudioStore((state) => state.token);

  if (!token) {
    // Redirect to main site login (use 'return' param which login page expects)
    const returnUrl = encodeURIComponent('https://www.mypodops.com/studio-v2');
    window.location.href = `https://www.mypodops.com/login?return=${returnUrl}`;
    return null;
  }

  return <>{children}</>;
}

// Main App Component
export default function App() {
  const [loading, setLoading] = useState(true);
  const setAuth = useStudioStore((state) => state.setAuth);

  useEffect(() => {
    // Check for auth token in URL (from OAuth redirect)
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    const urlUserId = params.get('userId');
    const urlUserName = params.get('userName');

    if (urlToken && urlUserId) {
      setAuth(urlToken, urlUserId, urlUserName || 'User');
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }

    setLoading(false);
  }, [setAuth]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading Studio...</p>
      </div>
    );
  }

  return (
    <BrowserRouter basename="/studio-v2">
      <div className="studio-app">
        <Routes>
          {/* Green Room - Pre-join setup */}
          <Route
            path="/"
            element={
              <RequireAuth>
                <GreenRoom />
              </RequireAuth>
            }
          />

          {/* Studio Room - Main recording interface */}
          <Route
            path="/room/:sessionId"
            element={
              <RequireAuth>
                <StudioRoom />
              </RequireAuth>
            }
          />

          {/* Join via invite link */}
          <Route
            path="/join"
            element={<GreenRoom isGuest={true} />}
          />

          {/* Recordings management */}
          <Route
            path="/recordings"
            element={
              <RequireAuth>
                <RecordingsPage />
              </RequireAuth>
            }
          />

          {/* GoLive - Multi-platform livestreaming */}
          <Route
            path="/live"
            element={
              <RequireAuth>
                <GoLive />
              </RequireAuth>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}
